import './Payment.scss';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  completeAppointment,
  createAppointment,
  invoiceSummary,
  loadAppointment,
  loadAppointmentsRequests,
  patchAppointment,
} from 'actions/appointments';
import Button from 'atoms/button';
import toast from 'atoms/toast';
import useQuery from 'hooks/useQuery';
import { refreshAppointments } from 'organisms/Appointments/AppointmentActions/util';
import PaymentDrawerLoader from 'organisms/Loaders/PaymentDrawerLoader';
import {
  clearCheckoutForm,
  setAppointment,
  setCompleteItems,
  setIncompleteItems,
  setIsLoadingSummary,
  setService,
  setSummary,
} from 'store/reducers/checkout';
import {
  closeDrawer,
  requestFulfilled,
  requestOngoing,
  toggleCheckoutSuccessModal,
} from 'store/reducers/ui';
import { isEmpty } from 'util';

import { groomerToOption } from '../NewAppointment';
import AppointmentItemsList from './AppointmentItemsList';
import CustomDiscount from './CustomDiscount';
import Header from './Header';
import PaymentMethod from './PaymentMethod';
import ReassignDropdown from './ReassignDropdown';
import RebookAppointment from './RebookAppointment';
import Summary from './Summary';
import Tips from './Tips';
import { getRequestBody } from './util';

export default function PaymentDrawer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const {
    appointment,
    completeItems,
    customDiscount,
    customTip,
    selectedTip,
    service,
    summary,
  } = useSelector((state) => state.checkout);
  const {
    animal,
    dateStart,
    location: appointmentLocation,
    locationUser,
    note,
    user,
  } = appointment;
  const { uuid: appointmentUuid } = useSelector((state) => state.ui.drawer);
  const [reassignedGroomer, setReassignedGroomer] = useState({});
  const [isRebook, setIsRebook] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState({});
  const [paymentMethodType, setPaymentMethodType] = useState({});
  const isRebookValid =
    !isRebook ||
    (isRebook &&
      !isEmpty(selectedTimeSlot) &&
      !!selectedTimeSlot.date &&
      !!selectedTimeSlot.timeStart);

  useEffect(() => {
    if (dateStart) {
      const [date, time] = dateStart.split('T');
      setSelectedTimeSlot({
        date: dayjs(date).add(6, 'week').format('YYYY-MM-DD'),
        timeStart: time,
      });
    }
  }, [dateStart]);

  useEffect(() => {
    dispatch(loadAppointment({ apuid: appointmentUuid })).then(
      ({ data, success }) => {
        if (success) {
          const { service, summary, addOns } = data.data;
          const { name, uuid, price } = service;
          dispatch(setAppointment(data.data));
          dispatch(setService({ uuid, name, price }));
          dispatch(setSummary(summary));
          setReassignedGroomer(groomerToOption(data?.data?.locationUser));
          if (addOns) {
            dispatch(setCompleteItems(addOns));
            dispatch(setIncompleteItems(addOns));
          }
        }
      }
    );

    return () => dispatch(clearCheckoutForm());
  }, []);

  // Update summary
  useEffect(() => {
    if (service.uuid && service.name && service.price) {
      dispatch(setIsLoadingSummary(true));
      const body = getRequestBody({
        service,
        appointment,
        completeItems,
        customDiscount,
        selectedTip,
        customTip,
      });

      dispatch(
        invoiceSummary({
          apuid: appointment.uuid,
          body,
        })
      ).then(({ success, data }) => {
        // TODO: Create a (better) global system for this. We want loaders
        // to display for at least 400ms regardless of API request completion time
        setTimeout(() => {
          setIsLoadingSummary(false);
        }, 400);
        if (success) {
          dispatch(setSummary(data.data.summary));
        }
      });
    }
  }, [
    completeItems,
    appointment,
    customTip,
    selectedTip,
    service,
    customDiscount,
  ]);

  const reassignAppointment = async () =>
    dispatch(
      patchAppointment(
        appointment.uuid,
        {
          uuid: {
            animal: animal?.uuid,
            location: appointmentLocation?.uuid,
            location_user: reassignedGroomer?.uuid,
            service: service?.uuid,
          },
          date_start: dateStart,
          note,
        },
        true
      )
    );

  const checkoutAppointment = async () => {
    const body = getRequestBody({
      service,
      appointment,
      completeItems,
      customDiscount,
      selectedTip,
      customTip,
      paymentMethodType,
    });

    const apptCompleted = await dispatch(
      completeAppointment({
        apuid: appointment.uuid,
        body,
      })
    );
    dispatch(requestFulfilled());

    if (apptCompleted.success) {
      if (window.location.pathname.includes('pet-profile')) {
        const [_, petId] = window.location.pathname.split('pet-profile/');
        dispatch(loadAppointmentsRequests(petId));
      }
      refreshAppointments({
        location,
        query,
        dispatch,
        auid: animal?.uuid,
      });
      if (paymentMethodType.value === 'Card') {
        dispatch(
          toggleCheckoutSuccessModal({
            isOpen: true,
            user: {
              firstName: appointment.user.firstName,
              lastName: appointment.user.lastName,
            },
            service: {
              name: appointment.service.name,
              amount: summary.total.localized,
            },
          })
        );
      } else toast({ text: 'Appointment completed successfully' });
    }
  };

  const rebookAppointment = async () =>
    dispatch(
      createAppointment({
        animalUuid: animal?.uuid,
        locationUuid: appointmentLocation?.uuid,
        locationUserUuid: locationUser?.uuid,
        serviceUuid: service?.uuid,
        userUuid: user?.uuid,
        note,
        dateStart: `${selectedTimeSlot.date}T${selectedTimeSlot.timeStart}`,
        appointmentSource: 'rebook',
      })
    ).then(({ success }) => {
      if (success) toast({ text: 'Appointment rebooked!' });
      else
        toast({
          text: 'Appointment could not be rebooked, please try again later',
        });
    });

  const onSubmit = async () => {
    if (service) {
      dispatch(requestOngoing());
      if (reassignedGroomer?.uuid !== locationUser?.uuid) {
        await reassignAppointment();
      }
      await checkoutAppointment();
      if (isRebook) await rebookAppointment();
      dispatch(closeDrawer());
    }
  };

  if (isEmpty(appointment) || isEmpty(summary))
    return (
      <div className="payment-drawer">
        <PaymentDrawerLoader />
      </div>
    );

  return (
    <div className="payment-drawer flex-column align-end">
      <div className="w-100">
        <h2 className="mb-25 f26">Finalize payment</h2>
        <ReassignDropdown
          selectedGroomer={reassignedGroomer}
          setSelectedGroomer={setReassignedGroomer}
        />
        <Header />
        <AppointmentItemsList />
        <CustomDiscount />
        <Tips />
        <Summary />
        <PaymentMethod
          setPaymentMethod={setPaymentMethodType}
          paymentMethod={paymentMethodType}
        />
        <RebookAppointment
          isRebook={isRebook}
          setIsRebook={setIsRebook}
          selectedTimeSlot={selectedTimeSlot}
          setSelectedTimeSlot={setSelectedTimeSlot}
          selectedGroomer={reassignedGroomer}
        />
      </div>
      <Button
        size="medium"
        displayLoaderIcon
        variant={!service.name ? 'disabled' : 'default'}
        text={`Charge ${
          !service.name || !summary ? '' : summary?.total?.localized
        }`}
        onClick={onSubmit}
        disabled={!isRebookValid}
      />
    </div>
  );
}
